import React from 'react'
import { getCookie, APPConfig } from 'config/appConfig'
import { IS_BROWSER, convertToBoolean, getLocalePathParam } from 'src/utils'
import ContentStack from 'src/api/contentStack'

/** Same allowlist as seoTagSerializer (defense-in-depth vs tampered seoTags). */
const ALLOWED_SEO_TYPES = new Set(['title', 'meta', 'link', 'script'])

/**
 * Same strip as seoTagSerializer: drop XSS / warning-suppression props before
 * spreading into React.createElement for Helmet (defense-in-depth vs tampered seoTags).
 *
 * @param {object} props
 * @returns {object}
 */
function omitDangerousSeoProps(props) {
  if (!props || typeof props !== 'object') return {}
  const {
    dangerouslySetInnerHTML,
    suppressContentEditableWarning,
    suppressHydrationWarning,
    ...safeProps
  } = props
  return safeProps
}

/**
 * Helmet list key from tag identity (type + name/rel/href/…), not array index.
 * Index keys fail Sonar and would shuffle if tags were filtered.
 *
 * @param {string} type Host tag name (title/meta/link/script)
 * @param {object} props Attribute props after omitDangerousSeoProps (no children)
 * @returns {string}
 */
function seoTagElementKey(type, props) {
  return [
    'mfe-seo',
    type,
    props.name,
    props.property,
    props.rel,
    props.href,
    props.content,
    props.type,
  ]
    .filter(part => part != null && part !== '')
    .join(':')
}

/**
 * Reconstructs React elements from the JSON-safe { type, props } descriptors
 * produced by the child SSR process (see serverRemoteLoaderChild.js). Does not
 * write to the DOM; the caller passes the array into <Helmet>.
 *
 * @param {Array<{ type: string, props?: { children?: string|number|boolean|null|Array<string|number|boolean|null> } }>} seoTags
 *   Only allowlisted tags (title/meta/link/script) with primitive children
 *   (or arrays of primitives) are supported. Nested descriptor objects and
 *   other host tags (e.g. base, style) are dropped, not converted recursively.
 *   `dangerouslySetInnerHTML` and related warning flags are omitted.
 *   React keys come from seoTagElementKey (attributes), not list index.
 * @returns {Array} Always an array (empty when input is missing or invalid).
 */
export function deserializeSeoTags(seoTags) {
  if (!Array.isArray(seoTags)) return []
  return seoTags
    .filter(
      tag => tag && typeof tag.type === 'string' && ALLOWED_SEO_TYPES.has(tag.type)
    )
    .map(tag => {
      const { children, ...restProps } = omitDangerousSeoProps(tag.props || {})
      return React.createElement(
        tag.type,
        { key: seoTagElementKey(tag.type, restProps), ...restProps },
        toPrimitiveChildren(children)
      )
    })
}

function isPrimitiveChild(child) {
  return (
    child == null ||
    typeof child === 'string' ||
    typeof child === 'number' ||
    typeof child === 'boolean'
  )
}

function toPrimitiveChildren(children) {
  if (isPrimitiveChild(children)) return children
  if (Array.isArray(children)) {
    const primitives = children.filter(isPrimitiveChild)
    if (
      primitives.length !== children.length &&
      process.env.NODE_ENV !== 'production'
    ) {
      console.warn(
        '[MFE SSR] deserializeSeoTags: nested non-primitive children are not supported and were dropped'
      )
    }
    return primitives
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      '[MFE SSR] deserializeSeoTags: nested non-primitive children are not supported and were dropped'
    )
  }
  return undefined
}

/**
 * Returns the canonical map key used to look up a specific MFE app+component
 * pair in context maps (mfeAppsMap, mfeServerDataMap, etc.).
 */
export const mfeAppKey = (appName, component) =>
  `${appName || ''}:${component || 'App'}`

/**
 * Builds a stable DOM id for a MFE loader slot, used to locate the
 * server-rendered HTML node on hydration.
 */
export const buildMfeLoaderId = ({ index, appName, component } = {}) => {
  const safeAppName = appName || 'mfe'
  const safeComponent = component || 'App'
  if (Number.isInteger(index) && index >= 0) {
    return `mfe-loader-${index}-${safeAppName}-${safeComponent}`
  }
  return `mfe-loader-0-${safeAppName}-${safeComponent}`
}

/**
 * Strips query-string and hash from a URL, and removes trailing slashes so
 * that path comparisons are stable.
 */
export function normalizePath(url) {
  const pathOnly = (url || '').split('#')[0].split('?')[0] || ''
  if (!pathOnly) return ''
  if (pathOnly.length <= 1) return pathOnly
  let end = pathOnly.length
  while (pathOnly[end - 1] === '/') end--
  return pathOnly.slice(0, end)
}

/**
 * Returns true when the SSR request URL and the current browser pathname
 * refer to the same page (ignoring query-string, hash, and trailing slashes).
 */
export function isSamePath(ssrUrl, browserPathname) {
  return normalizePath(ssrUrl) === normalizePath(browserPathname)
}

export async function getMFEConfig(options) {
  const { req, applicationName, component } = options || {}
  let hostName = ''
  let remoteConfig = {}
  const referenceId = 'microfrontend_config'
  const uid = ''
  const getReferenceOptions = {
    isToSkipIncludes: true,
    includeFallback: true,
  }

  if (req) {
    hostName = req?.headers?.host || req?.hostname || ''
  }

  const locationProps = {
    staticContext: {
      originalUrl: req?.originalUrl || '',
    },
  }

  const response = await ContentStack.getReference(
    referenceId,
    uid,
    locationProps,
    getReferenceOptions
  )

  return response
}

export function microFrontendConfigFromCS(options) {
  const { response, applicationName, component, hostName, isBrowser } =
    options || {}
  let remoteConfig = {}

  // response can be either a single entry or { entries: [...] }
  const entry = response?.entries?.[0] || response
  const mfeAppConfig = entry?.microfrontend_app_config?.find(
    mfeConfig => mfeConfig?.app_name === applicationName
  )

  if (mfeAppConfig) {
    // isBrowser can be explicitly passed (e.g. when computing the client URL
    // from a server-side context).  Falls back to the module-level IS_BROWSER.
    const useBrowserEnv = isBrowser === undefined ? IS_BROWSER : isBrowser
    const env =
      getEnvironment({ hostName }) + (useBrowserEnv ? '' : '_server') + '_url'
    const appName = mfeAppConfig?.app_name
    const s3Path = mfeAppConfig?.s3_path_name
    const pathName = s3Path || appName
    let mfeUrl = mfeAppConfig?.[env]

    let redirectedApps = []
    let cookieList = {}

    try {
      const appRedirects = getCookie('appRedirects')
      if (appRedirects) {
        const appRedirectCookie = JSON.parse(
          decodeURIComponent(appRedirects || '')
        )
        if (appRedirectCookie?.static) {
          cookieList = appRedirectCookie?.static || {}
          redirectedApps = Object.keys(appRedirectCookie?.static) || []
        }
      }
    } catch (error) {
      console.log('Error parsing appRedirects cookie:', error)
    }

    if (redirectedApps?.includes(`static/${pathName}`)) {
      mfeUrl = redirectAppsDomain({ mfeUrl, appName: pathName, cookieList })
    }

    remoteConfig = {
      name: appName,
      entry: mfeUrl,
      component,
    }
  }

  return remoteConfig
}

export function redirectAppsDomain(options) {
  const { mfeUrl, appName, cookieList } = options
  const { appsPathPrefix, enableSFAppsPath } = APPConfig?.getAppConfig()
  const activeEnvironment = APPConfig.getLocalEnv()
  const featureBranchName = cookieList?.[`static/${appName}`]

  if (convertToBoolean(enableSFAppsPath) && mfeUrl) {
    let domain = ''
    if (IS_BROWSER) {
      domain = window.location.hostname || document?.domain || ''
    }
    const isLocal = domain.includes('local.') || domain.includes('localhost')
    const prefix = isLocal ? `https://${activeEnvironment}` : ''
    const locale = getLocalePathParam()
    const localePrefix = locale ? `/${locale}` : ''

    try {
      const url = new URL(mfeUrl)
      let pathname = url.pathname.startsWith('/')
        ? url.pathname.slice(1)
        : url.pathname

      if (featureBranchName) {
        pathname = pathname.replace(
          `/${appName}/`,
          `/${appName}/${featureBranchName}/`
        )
      }
      return `${prefix}${localePrefix}/mfe/${pathname}`
    } catch (e) {
      console.log('>>> Error while parsing MFE URL for proxy:', e)
    }
  }

  let newMfeUrl = mfeUrl
  // Redirecting to feature branch S3 path instead of default path
  try {
    if (mfeUrl && IS_BROWSER && appsPathPrefix) {
      newMfeUrl = mfeUrl?.replace(
        /^(https?:\/\/)([^\/]+)/,
        `$1${appsPathPrefix}`
      )
      if (featureBranchName) {
        newMfeUrl = newMfeUrl?.replace(
          `/${appName}/`,
          `/${appName}/${featureBranchName}/`
        )
      }
      return newMfeUrl
    } else {
      return newMfeUrl
    }
  } catch (error) {
    console.log('>>> Error while redirecting to apps domain:', error)
  }
}

export function getEnvironment(options) {
  const { hostName } = options || {}
  let env = 'prod'
  let domain = hostName || ''

  if (IS_BROWSER) {
    domain = document?.domain || ''
  }

  if (domain.includes('local.') || domain.includes('localhost')) {
    return 'test'
  }

  if (
    domain.includes('test.') ||
    domain.includes('test-beta.') ||
    domain.includes('test-hotfix.') ||
    domain.includes('sit.')
  ) {
    return 'test'
  }

  if (domain.includes('dev.')) {
    return 'dev'
  }

  return 'prod'
}

/**
 * Returns true when the MFE server envelope contains meaningful data that the
 * client can use to skip a redundant getServerSideProps call on hydration.
 *
 * Prefers the authoritative `_serverDataAvailable` flag stamped by mfeServer.js.
 * Falls back to the structural heuristic for envelopes produced outside that
 * path (e.g. tests, legacy code).
 *
 * @param {object} mfeData  - The mfeAppData / mfeServerData envelope.
 * @returns {boolean}
 */
export function isMfeServerDataAvailable(mfeData) {
  if (!mfeData || typeof mfeData !== 'object') return false
  // Prefer the explicit flag stamped by the server.
  if ('_serverDataAvailable' in mfeData)
    return Boolean(mfeData._serverDataAvailable)
  // Structural fallback: valid envelope with at least one non-nullish prop.
  return (
    '_routing' in mfeData &&
    Object.values(mfeData.props || {}).some(v => v !== null && v !== undefined)
  )
}

/**
 * Normalizes a raw getServerSideProps result into the standard MFE envelope.
 *
 * New envelope format:
 *   { _routing: { pageStatus, redirectionUrl, priority }, props: {} }
 *
 * Legacy flat format (backward compat):
 *   { isPageFound, redirectionUrl, ...componentProps }
 *
 * @param {object|null} raw
 * @returns {{ _routing: object, props: object }}
 */
export function normalizeMfeResult(raw) {
  if (!raw) return null

  // New envelope format — has _routing or explicit props key
  if (
    raw._routing !== undefined ||
    Object.prototype.hasOwnProperty.call(raw, 'props')
  ) {
    return {
      _routing: {
        pageStatus: raw._routing?.pageStatus ?? 'unknown',
        redirectionUrl: raw._routing?.redirectionUrl ?? null,
        priority: raw._routing?.priority ?? 0,
      },
      props: raw.props ?? {},
    }
  }

  // Legacy flat format — migrate automatically
  const { isPageFound, redirectionUrl, ...rest } = raw
  return {
    _routing: {
      pageStatus:
        isPageFound === false
          ? 'not_found'
          : isPageFound === true
            ? 'found'
            : 'unknown',
      redirectionUrl: redirectionUrl ?? null,
      priority: 0,
    },
    props: rest,
  }
}
